import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

import { GetglobalsettingsService } from './getglobalsettings.service';
import {
  AgentDashboardSummary,
  AgentDashboardTrendPoint,
  AgentDashboardGranularity,
  AgentDashboardSla,
  AgentDashboardRecentItem,
  AgentDashboardActionResult,
} from '../shared/agent-dashboard';

/**
 * REST client for API-13 (Agent ops dashboard), `/api/agent/dashboard/*` on
 * the webapi backend — `realm:portal-admin` only, same pattern as
 * `agent-admin-api.service.ts`. Originally read-only reporting; also has
 * the three `POST .../claim|ignore|spam` actions now -- no `retry()` on
 * those (unlike the GETs below), since a retried POST could double-fire a
 * write against a request that only ever needed one. Errors are not
 * caught/alerted here — callers subscribe with their own `error` handler.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentDashboardApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + url;
  }

  private _options(params?: Record<string, string>) {
    return {
      headers: this.globalsettingsservice.getStandardApiHeaders(),
      ...(params ? { params } : {}),
    };
  }

  getSummary(from: string, to: string): Observable<AgentDashboardSummary> {
    return this.http.get<AgentDashboardSummary>(this._getApiUrl('/api/agent/dashboard/summary'), this._options({ from, to }))
      .pipe(retry(1));
  }

  getTrend(from: string, to: string, granularity: AgentDashboardGranularity): Observable<AgentDashboardTrendPoint[]> {
    return this.http.get<AgentDashboardTrendPoint[]>(this._getApiUrl('/api/agent/dashboard/trend'), this._options({ from, to, granularity }))
      .pipe(retry(1));
  }

  getSla(from: string, to: string): Observable<AgentDashboardSla> {
    return this.http.get<AgentDashboardSla>(this._getApiUrl('/api/agent/dashboard/sla'), this._options({ from, to }))
      .pipe(retry(1));
  }

  getRecent(limit: number): Observable<AgentDashboardRecentItem[]> {
    return this.http.get<AgentDashboardRecentItem[]>(this._getApiUrl('/api/agent/dashboard/recent'), this._options({ limit: String(limit) }))
      .pipe(retry(1));
  }

  claimRequest(id: number): Observable<AgentDashboardActionResult> {
    return this.http.post<AgentDashboardActionResult>(this._getApiUrl(`/api/agent/dashboard/requests/${id}/claim`), {}, this._options());
  }

  ignoreRequest(id: number, reason: string): Observable<AgentDashboardActionResult> {
    return this.http.post<AgentDashboardActionResult>(this._getApiUrl(`/api/agent/dashboard/requests/${id}/ignore`), { reason }, this._options());
  }

  spamRequest(id: number): Observable<AgentDashboardActionResult> {
    return this.http.post<AgentDashboardActionResult>(this._getApiUrl(`/api/agent/dashboard/requests/${id}/spam`), {}, this._options());
  }
}
